import {ChangeDetectionStrategy, Component, forwardRef, OnInit,} from '@angular/core';
import {MatIconModule} from '@angular/material/icon';
import {NgForOf} from "@angular/common";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from "@angular/forms";
import {environment} from 'environments/environment';

type SocialLinksMap = { [key: string]: string };

export interface SocialLink {
    platform: string;
    name: string;
    url: string;
    icon: string;
    placeholder: string;
    readOnly?: boolean;
}

@Component({
    selector: 'app-social-links-editor',
    templateUrl: './social-links-editor.component.html',
    styleUrl: './social-links-editor.component.scss',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgForOf,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SocialLinksEditorComponent),
            multi: true
        }
    ]
})
export class SocialLinksEditorComponent implements OnInit, ControlValueAccessor {
    internalSocials: SocialLink[] = [];
    isDisabled: boolean = false;

    onChange: (value: string) => void = () => {};
    onTouched: () => void = () => {};

    private readonly SOCIAL_PLATFORMS: Omit<SocialLink, 'url'>[] = [
        { platform: 'linkedin', name: 'LinkedIn', icon: 'linkedin', placeholder: 'https://linkedin.com/in/your-profile' },
        { platform: 'facebook', name: 'Facebook', icon: 'facebook', placeholder: 'https://facebook.com/your-page' },
        { platform: 'twitter', name: 'Twitter (X)', icon: 'twitter', placeholder: 'https://twitter.com/your-handle' },
        { platform: 'instagram', name: 'Instagram', icon: 'instagram', placeholder: 'https://instagram.com/your-username' },
        { platform: 'whatsapp', name: 'WhatsApp', icon: 'whatsapp', placeholder: 'https://wa.me/15551234567' },
        { platform: 'ghl_chat', name: 'GoHighLevel Widget', icon: 'chat', placeholder: 'Widget Script or ID' }
    ];

    constructor() { }

    ngOnInit(): void {
        this.initializeSocials(null);
    }

    writeValue(jsonString: string | null): void {
        this.initializeSocials(jsonString);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    private initializeSocials(value: any): void {
        let parsedLinks: SocialLinksMap = {};

        // 1. Handle if value is valid, determine if Object or String
        if (value) {
            if (typeof value === 'object') {
                // Already an object (API client might have parsed it automatically)
                parsedLinks = value;
            } else if (typeof value === 'string') {
                try {
                    parsedLinks = JSON.parse(value);
                } catch (e) {
                    console.warn('SocialLinksEditor: standard parse failed, attempting fallback for single quotes.');
                    try {
                        // Fallback: This handles JSON stored with single quotes: {'key':'val'}
                        // Note: This is a basic repair, might fail on complex URLs with single quotes inside them.
                        const validJson = value.replace(/'/g, '"');
                        parsedLinks = JSON.parse(validJson);
                    } catch (e2) {
                        console.error('CRITICAL: Could not parse Social Links JSON:', value);
                        parsedLinks = {};
                    }
                }
            }
        }

        // 2. Logic to detect missing GHL and auto-save
        const isGhlMissing = !Object.prototype.hasOwnProperty.call(parsedLinks, 'ghl_chat');

        this.internalSocials = this.SOCIAL_PLATFORMS.map(platform => {
            const isGhl = platform.platform === 'ghl_chat';
            // Only set readOnly if it is GHL AND ambassadorMode is true
            const shouldBeReadOnly = isGhl && environment.user_ui_type === 4;

            return {
                ...platform,
                url: parsedLinks[platform.platform] || '',
                readOnly: shouldBeReadOnly
            };
        });

        // 3. Trigger save if GHL was missing
        if (isGhlMissing) {
            setTimeout(() => this.onLinkChange(), 0);
        }
    }


    onLinkChange(): void {
        this.onTouched();
        const updatedLinksMap = this.internalSocials.reduce((acc, social) => {
            if (social.url && social.url.trim() !== '') {
                acc[social.platform] = social.url.trim();
            }
            return acc;
        }, {} as SocialLinksMap);

        this.onChange(JSON.stringify(updatedLinksMap));
    }

    trackByPlatform(index: number, item: SocialLink): string {
        return item.platform;
    }
}
